import { capitalize } from 'lodash';
import { useRouter } from 'next/router';

import { Button } from '@/components/button';
import { Text } from '@/components/text';
import { RouteSummaryModel } from '@/lib/models/route-summary/types';
import { LocationInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { convertModeToTransportType } from '@/utils/format';
import { createScheduleSearchPageUrl, createSearchPageUrlUsingKeys } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

interface Props {
  mode: SegmentModeType;
  from: LocationInterface;
  to: LocationInterface;
  route: RouteSummaryModel;
}

const PageRouteCta = ({ mode, from, to, route }: Props) => {
  const router = useRouter();
  const { origin, destination } = router.query;

  const { origin: originPort, destination: destinationPort } = route;

  const handleMoreDetailsClick = () => {
    // Analytics Event
    trackUser.event('RoutePage Mode CTA', {
      mode,
      origin: from.shortName,
      destination: to.shortName,
    });
  };

  const handleSchedulesClick = () => {
    // Analytics Event
    trackUser.event('RoutePage Schedules CTA', {
      mode,
      origin: from.shortName,
      destination: to.shortName,
    });
  };

  const { base, textWrapper, buttonWrapper } = styles();

  return (
    <div className={base()} data-cy="page-routes-cta">
      <div className={textWrapper()}>
        <Text className="m-0 font-medium">
          {capitalize(convertModeToTransportType(mode))} routes from {from.name} to {to.name}
        </Text>
        <Text variant="secondary" className="m-0">
          Explore more shipping routes including schedules and transit times.
        </Text>
      </div>
      <div className={buttonWrapper()}>
        <Button
          href={createSearchPageUrlUsingKeys(origin, destination)}
          target="_blank"
          size="small"
          onClick={handleMoreDetailsClick}
        >
          Explore routes
        </Button>
        {originPort && destinationPort && (
          <Button
            href={createScheduleSearchPageUrl({ origin: originPort, destination: destinationPort, mode })}
            target="_blank"
            size="small"
            iconRight="arrow-right-alt"
            onClick={handleSchedulesClick}
          >
            See schedules
          </Button>
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative -mx-6 mt-12 border-y border-grey-200 px-8 py-4 md:mx-0 md:flex md:flex-row md:items-center md:rounded-lg md:border md:shadow-soft',
    textWrapper: 'mb-4 mt-2 flex-1 md:m-0',
    buttonWrapper: 'flex gap-4',
  },
});

export { PageRouteCta };
